import { Button } from "@/components/ui/button"
import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType as NewEventType } from "@/features/events/types/EventType"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import CloseButton from "../../../pages/new-platform/components/CloseButton"
import { saveEvent } from "../../../utils/apis/user-service"
import { NEW_PLATFORM_DIAGNOSTICA_LOGO_BLACK } from "../../../utils/Constants"
import { EventType } from "../../../utils/enums/EventType"
import { useAuthStore } from "../../../utils/stores/useAuthStore"
import { useSurveyDataStore } from "../../../utils/stores/useSurveyDataStore"
import { useUserStore } from "../../../utils/stores/useUserStore"
import LoadingSpinner from "../../shared/components/Loaders"
import { useInitializeSurveyQuestions } from "../hooks/useInitializeSurveyQuesitions"

// Header Component
const Header = ({ onClose }: { onClose: () => void }) => (
    <div className="w-full min-h-[80px] py-4 flex items-center justify-center border-b border-solid border-black md:min-h-[120px]">
        <div className="w-full flex items-center justify-between p-4">
            <div className="flex items-center">
                <img className="mr-[10px] w-8 h-8 md:w-10 md:h-10" src="/images/new-platform/logos/holifya-logo-no-text.svg" alt="holifya-logo" />
                <div className="font-bold text-[12px] leading-[17px] md:text-sm">Questionario | Health Assessment</div>
            </div>
            <CloseButton onClick={onClose} />
        </div>
    </div>
)

// Content Component
const Content = () => (
    <div className="w-full p-5 font-normal text-[16px] leading-[26px] md:p-6">
        Rispondi ora al questionario di 📝 <strong>Health Assessment</strong> per fornire tutte le informazioni necessarie per personalizzare al massimo le indicazioni che puoi ricevere nel tuo Percorso Health, se ne hai acquistato uno, e dalla Medical Equipe durante i consulti con Medico e Nutrizionista.
        <br />
        Dovrai rispondere a questo primo questionario completo una sola volta. Potrai poi rispondere al <strong>questionario di check-up</strong> per aggiornare e tenere traccia del tuo stato di salute e delle tue abitudini durante tutto il tuo percorso con Holifya.
        <br />
        Puoi modificare le singole risposte in qualsiasi momento accedendo a Health Assessment da <strong>Diagnostica</strong> ( <span><img className="align-middle inline-block m-0 p-0 h-auto w-auto" src={NEW_PLATFORM_DIAGNOSTICA_LOGO_BLACK} /></span> ).
    </div>
)

// Footer Component
const Footer = ({ onProceed, onBack }: { onProceed: () => void; onBack: () => void }) => (
    <div className="w-full min-h-[100px] p-4 flex items-center justify-center border-t border-solid border-black">
        <div className="w-full flex flex-col gap-4 md:flex-row md:justify-between md:gap-6">
            <Button size="xl" className="w-full md:w-[362px]" onClick={onProceed}>
                Inizia
            </Button>
            <Button variant="outline" size="xl" className="w-full md:w-[362px]" onClick={onBack}>
                Chiudi
            </Button>
        </div>
    </div>
)

const HealthAssessmentHomePage = () => {
    const { sendEvent } = useCreateEvent()
    const navigate = useNavigate()
    const { healthAssessmentIds } = useInitializeSurveyQuestions()
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const { userData } = useUserStore()
    const email = userData.email
    const { updateSurveyData } = useSurveyDataStore()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const onProceedClick = async () => {
        try {
            setIsLoading(true)
            setError(null)
            navigate("/health-assessment-survey")
            if (!email || !idToken) return
            
            await saveEvent(
                email,
                "go_to_health_assessment_button_click",
                () => {},
                (error: string) => setError(error),
                "health_assessment_home_page"
            )

            await sendEvent({
                event: NewEventType.BUTTON_CLICK,
                sub_category: "action_button",
                event_metadata: { action: "start_health_assessment"}
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        } finally {
            setIsLoading(false)
        }
    }

    const onBackClick = async () => {
        try {
            setIsLoading(true)
            setError(null)
            navigate("/home")
            if (!email || !idToken) return
            
            await saveEvent(
                email,
                "back_to_home_page_button_click",
                () => {},
                (error: string) => setError(error),
                "health_assessment_home_page"
            )

            await sendEvent({
                event: NewEventType.BUTTON_CLICK,
                sub_category: "back_button",
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const initializePage = async () => {
            try {
                setIsLoading(true)
                setError(null)
                if (!email || !idToken) return
                
                await saveEvent(
                    email,
                    EventType.PageView,
                    () => {},
                    (error: string) => setError(error),
                    "health_assessment_home_page"
                )

                await sendEvent({
                    event: NewEventType.PAGE_VIEW,
                    sub_category: "health_assessment_home_page",
                })

                if (healthAssessmentIds) {
                    updateSurveyData({ healthAssessmentQuestions: healthAssessmentIds })
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred')
            } finally {
                setIsLoading(false)
            }
        }

        initializePage()
    }, [])

    if (isLoading) {
        return (
            <div className="w-full min-h-screen flex items-center justify-center">
                <LoadingSpinner />
            </div>
        )
    }

    if (error) {
        return (
            <div className="w-full min-h-screen flex items-center justify-center">
                <div className="text-red-500">Error: {error}</div>
            </div>
        )
    }

    return (
        <div className="w-full h-screen flex flex-col fixed inset-0">
            <div className="flex-none">
                <Header onClose={() => navigate("/home")} />
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className="w-full flex flex-col items-center">
                    <Content />
                </div>
            </div>
            <div className="flex-none sticky bottom-0 z-10 bg-white">
                <Footer onProceed={onProceedClick} onBack={onBackClick} />
            </div>
        </div>
    )
}

export default HealthAssessmentHomePage
